import { lazy, Suspense } from "react";
import { ChunkErrorBoundary } from "app/chunk-error-boundary";
import { usePortalLanguage } from "app/use-portal-language";
import { useAttributionCapture } from "common/hooks/useAttributionCapture/useAttributionCapture";
import { useBrowserTabTitle } from "common/hooks/use-browser-tab-title/use-browser-tab-title";
import { useGTM } from "common/hooks/useGTM/useGTM";
import useInitViewer from "common/hooks/useInitViewer/useInitViewer";
import { useLocationChangeListener } from "common/hooks/useLocationChangeListener/useLocationChangeListener";
import { usePostPurchaseRedirect } from "common/hooks/usePostPurchaseRedirect/usePostPurchaseRedirect";
import { useAccountAnalyticsIdentity } from "entities/account";
import { useAnalyticsInit } from "shared/analytics";
import {
  ModalProvider,
  Spinner,
  NotFoundPage,
  UnauthorizedPage,
} from "dash-ui";
import ProtectedRoute from "flow-components/routes/ProtectedRoute/ProtectedRoute";
import { RoutesPath } from "flow-components/routes/routes";
import {
  Navigate,
  Route,
  Routes,
  matchPath,
  useLocation,
  useNavigate,
} from "react-router-dom";

// Client-facing pages follow the visitor's language, not the signed-in account's.
const VISITOR_LANGUAGE_ROUTES = [
  RoutesPath.PortfolioPreview,
  RoutesPath.PropertyInPortfolioPreview,
  RoutesPath.PortfolioPropertyPreview,
  RoutesPath.ShareProperty,
  RoutesPath.ShareFeaturedPortfolio,
  RoutesPath.FeaturedPortfolioPreview,
  RoutesPath.UnavailablePortfolioPage,
  RoutesPath.ExchangeEmbeddedPortfolio,
  RoutesPath.ExchangeEmbeddedProperty,
  RoutesPath.ExchangeEmbeddedSearch,
  RoutesPath.ExchangeEmbeddedMarketArea,
];

// Every route is code-split: the entry bundle carries the shell, not all 38 pages.
const AddAdditionalInfoPage = lazy(
  () =>
    import(
      "pages/authentication/complete-sign-up-scope/AddAdditionalInfoPage/AddAdditionalInfoPage"
    )
);
const PickSubscriptionPage = lazy(
  () =>
    import(
      "pages/authentication/complete-sign-up-scope/PickSubscriptionPage/PickSubscriptionPage"
    )
);
const StripeCheckoutResultPage = lazy(
  () =>
    import(
      "pages/authentication/complete-sign-up-scope/StripeCheckoutResultPage/StripeCheckoutResultPage"
    )
);
const ForgotPasswordPage = lazy(
  () => import("pages/authentication/ForgotPasswordPage/ForgotPasswordPage")
);
const ResetPasswordPage = lazy(
  () => import("pages/authentication/ResetPasswordPage/ResetPasswordPage")
);
const SignIn = lazy(() => import("pages/authentication/SignInPage/SignInPage"));
const SignInMfaPage = lazy(
  () => import("pages/authentication/SignInMfaPage/SignInMfaPage")
);
const DashboardPage = lazy(() => import("pages/Dashboard/Dashboard"));
const PortfolioDetailsPage = lazy(
  () => import("pages/portfolios/PortfolioDetailsPage/PortfolioDetailsPage")
);
const PortfolioPreviewPage = lazy(
  () => import("pages/portfolios/PortfolioPreviewPage/PortfolioPreviewPage")
);
const PortfolioPropertyPreviewPage = lazy(
  () =>
    import(
      "pages/portfolios/PortfolioPropertyPreviewPage/PortfolioPropertyPreviewPage"
    )
);
const PortfoliosPage = lazy(
  () => import("pages/portfolios/PortfoliosPage/PortfoliosPage")
);
const UnavailablePortfolioPage = lazy(
  () =>
    import("pages/portfolios/UnavailablePortfolioPage/UnavailablePortfolioPage")
);
const FeaturedPortfolioPreviewPage = lazy(
  () => import("pages/featured-portfolios/FeaturedPortfolioPreviewPage")
);
const ProfilePage = lazy(() => import("pages/ProfilePage/ProfilePage"));
const PropertiesPage = lazy(
  () => import("pages/properties/PropertiesPage/PropertiesPage")
);
const PropertiesSearchPage = lazy(
  () => import("pages/properties/PropertiesSearchPage/PropertiesSearchPage")
);
const PropertyPreviewAsClientPage = lazy(
  () =>
    import(
      "pages/properties/PropertyPreviewAsClientPage/PropertyPreviewAsClientPage"
    )
);
const PropertyPreviewPage = lazy(
  () => import("pages/properties/PropertyPreviewPage/PropertyPreviewPage")
);
const SharePropertyPreviewPage = lazy(
  () => import("pages/share/SharePropertyPreviewPage/SharePropertyPreviewPage")
);
const ShareFeaturedPortfolioPreviewPage = lazy(
  () =>
    import(
      "pages/share-portfolio/ShareFeaturedPortfolioPreviewPage/ShareFeaturedPortfolioPreviewPage"
    )
);
const TrackingPage = lazy(() => import("pages/TrackingPage/TrackingPage"));
const CodeConfirmationPage = lazy(() =>
  import("v2/pages/auth/code-confirmation/CodeConfirmationPage").then((m) => ({
    default: m.CodeConfirmationPage,
  }))
);
const AccountCreated = lazy(() =>
  import("v2/pages/auth/account-created").then((m) => ({
    default: m.AccountCreated,
  }))
);
const CompleteSignUpSSOPage = lazy(
  () => import("v2/pages/auth/sign-up/complete-sign-up-sso-page")
);
const SignUpPage = lazy(() =>
  import("v2/pages/auth/sign-up/page").then((m) => ({ default: m.SignUpPage }))
);
const SSOCallback = lazy(() =>
  import("v2/pages/auth/sso-callback").then((m) => ({ default: m.SSOCallback }))
);
const IdxDetailsPage = lazy(() =>
  import("v2/pages/idx/idx-details-page").then((m) => ({
    default: m.IdxDetailsPage,
  }))
);
const IdxEmbeddedPortfolioContainer = lazy(() =>
  import("v2/pages/idx/idx-embedded-container").then((m) => ({
    default: m.IdxEmbeddedPortfolioContainer,
  }))
);
const IdxEmbeddedPropertyPage = lazy(() =>
  import("v2/pages/idx/idx-embedded-property-page").then((m) => ({
    default: m.IdxEmbeddedPropertyPage,
  }))
);
const IdxEmbeddedPropertyPageV2 = lazy(() =>
  import("v2/pages/idx/idx-embedded-property-page-v2").then((m) => ({
    default: m.IdxEmbeddedPropertyPageV2,
  }))
);
const IdxEmbeddedSearchPage = lazy(() =>
  import("v2/pages/idx/idx-embedded-search-page").then((m) => ({
    default: m.IdxEmbeddedSearchPage,
  }))
);
const IdxPage = lazy(() =>
  import("v2/pages/idx/idx-page").then((m) => ({ default: m.IdxPage }))
);
const PrivacyPolicy = lazy(
  () => import("pages/flow/PrivacyPolicy/PrivacyPolicy")
);
const TermConditions = lazy(
  () => import("pages/flow/TermConditions/TermConditions")
);
const EmailLinkAccessPage = lazy(
  () => import("pages/authentication/EmailLinkAccessPage/EmailLinkAccessPage")
);
const ApiAccessAcceptPage = lazy(
  () => import("pages/authentication/ApiAccessAcceptPage/ApiAccessAcceptPage")
);

function PageFallback() {
  return (
    <div className="flex h-screen w-full items-center justify-center">
      <Spinner />
    </div>
  );
}

function App() {
  const [isViewerReady] = useInitViewer();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  usePortalLanguage({
    ignoreAccount: VISITOR_LANGUAGE_ROUTES.some((route) =>
      matchPath(route, pathname)
    ),
  });
  useBrowserTabTitle();
  useGTM();
  useAnalyticsInit();
  useAccountAnalyticsIdentity();
  useLocationChangeListener();
  useAttributionCapture();
  usePostPurchaseRedirect();

  return isViewerReady ? (
    <ChunkErrorBoundary>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route
            path="/"
            element={<Navigate replace to={RoutesPath.Dashboard} />}
          />
          <Route
            path={RoutesPath.Dashboard}
            element={
              <ProtectedRoute withBreadcrumbs={false} showContent>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutesPath.Profile}
            element={
              <ProtectedRoute
                withContainer={false}
                customPermissions={{
                  account: true,
                  verifiedAccount: true,
                  hasSubscription: false,
                }}
              >
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutesPath.Properties}
            element={
              <ProtectedRoute showContent>
                <PropertiesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutesPath.PropertiesSearch}
            element={
              <ProtectedRoute showContent>
                <PropertiesSearchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutesPath.PropertyPreview}
            element={
              <ProtectedRoute withContainer={false} showContent>
                <PropertyPreviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutesPath.PropertyPreviewAsClient}
            element={
              <ProtectedRoute withContainer={false}>
                <PropertyPreviewAsClientPage />
              </ProtectedRoute>
            }
          />

          <Route
            path={RoutesPath.PortfolioPreview}
            element={
              <ModalProvider>
                <PortfolioPreviewPage />
              </ModalProvider>
            }
          />
          <Route
            path={RoutesPath.FeaturedPortfolioPreview}
            element={
              <ModalProvider>
                <FeaturedPortfolioPreviewPage />
              </ModalProvider>
            }
          />
          <Route
            path={RoutesPath.PropertyInPortfolioPreview}
            element={
              <ModalProvider>
                <PortfolioPropertyPreviewPage isInPortfolioFolder />
              </ModalProvider>
            }
          />
          <Route
            path={RoutesPath.PortfolioPropertyPreview}
            element={
              <ModalProvider>
                <PortfolioPropertyPreviewPage />
              </ModalProvider>
            }
          />

          <Route
            path={RoutesPath.ShareProperty}
            element={
              <ModalProvider>
                <SharePropertyPreviewPage />
              </ModalProvider>
            }
          />
          <Route
            path={RoutesPath.ShareFeaturedPortfolio}
            element={
              <ModalProvider>
                <ShareFeaturedPortfolioPreviewPage />
              </ModalProvider>
            }
          />

          <Route
            path={RoutesPath.Portfolios}
            element={
              <ProtectedRoute>
                <PortfoliosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutesPath.PortfolioDetails}
            element={
              <ProtectedRoute>
                <PortfolioDetailsPage />
              </ProtectedRoute>
            }
          />

          <Route path={RoutesPath.SignIn} element={<SignIn />} />
          <Route path={RoutesPath.SignInMfa} element={<SignInMfaPage />} />
          <Route path={RoutesPath.SignUp} element={<SignUpPage />} />
          <Route
            path={RoutesPath.CompleteSignUpSSO}
            element={
              <ProtectedRoute
                withContainer={false}
                withProfileDropdown={false}
                customPermissions={{
                  account: true,
                  verifiedAccount: false,
                  hasSubscription: false,
                }}
              >
                <CompleteSignUpSSOPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutesPath.CompleteSignUpCodeConfirmation}
            element={
              <ProtectedRoute
                withContainer={false}
                customPermissions={{
                  account: true,
                  verifiedAccount: false,
                  hasSubscription: false,
                }}
              >
                <CodeConfirmationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutesPath.CompleteSignUpPickSubscription}
            element={
              <ProtectedRoute
                withContainer={false}
                customPermissions={{
                  account: true,
                  verifiedAccount: true,
                  hasSubscription: false,
                  ssoBlock: true,
                }}
              >
                <ModalProvider>
                  <PickSubscriptionPage />
                </ModalProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutesPath.CompleteSignUpAddAdditionalInfo}
            element={
              <ProtectedRoute
                withBreadcrumbs={false}
                withContainer={false}
                customPermissions={{
                  account: true,
                  verifiedAccount: true,
                  hasSubscription: false,
                  ssoBlock: true,
                }}
              >
                <AddAdditionalInfoPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutesPath.StripeCheckoutResult}
            element={
              <ProtectedRoute
                withContainer={false}
                withBreadcrumbs={false}
                customPermissions={{
                  account: true,
                  verifiedAccount: true,
                  hasSubscription: false,
                  ssoBlock: true,
                }}
              >
                <StripeCheckoutResultPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutesPath.ForgotPassword}
            element={<ForgotPasswordPage />}
          />
          <Route
            path={RoutesPath.ResetPassword}
            element={<ResetPasswordPage />}
          />
          <Route
            path={RoutesPath.EmailLinkAccess}
            element={<EmailLinkAccessPage />}
          />
          <Route
            path={RoutesPath.ApiAccessAccept}
            element={<ApiAccessAcceptPage />}
          />
          <Route
            path={RoutesPath.UnavailablePortfolioPage}
            element={<UnavailablePortfolioPage />}
          />
          <Route
            path={RoutesPath.Watchlist}
            element={
              <ProtectedRoute showContent>
                <TrackingPage />
              </ProtectedRoute>
            }
          />
          {/* Legacy URL of the Watchlist page (DSH-889) */}
          <Route
            path="/saved"
            element={<Navigate to={RoutesPath.Watchlist} replace />}
          />
          <Route
            path={RoutesPath.TermConditions}
            element={<TermConditions />}
          />
          {/* Redirect legacy Terms of Service URL to the renamed path (DSH-875) */}
          <Route
            path="/term-conditions"
            element={<Navigate replace to={RoutesPath.TermConditions} />}
          />
          <Route path={RoutesPath.PrivacyPolicy} element={<PrivacyPolicy />} />
          <Route
            path="*"
            element={
              <NotFoundPage onRedirect={() => navigate(RoutesPath.Dashboard)} />
            }
          />

          {/* IDX Routes */}
          <Route
            path={RoutesPath.Exchange}
            element={
              <ProtectedRoute>
                <IdxPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutesPath.ExchangeDetails}
            element={
              <ProtectedRoute>
                <IdxDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={RoutesPath.ExchangeEmbeddedPortfolio}
            element={<IdxEmbeddedPortfolioContainer />}
          />
          <Route
            path={RoutesPath.ExchangeEmbeddedMarketArea}
            element={<IdxEmbeddedPortfolioContainer />}
          />
          <Route
            path={RoutesPath.ExchangeEmbeddedProperty}
            element={
              <ModalProvider>
                <IdxEmbeddedPropertyPageV2 />
              </ModalProvider>
            }
          />
          <Route
            path={RoutesPath.ExchangeEmbeddedSearch}
            element={
              <ModalProvider>
                <IdxEmbeddedSearchPage />
              </ModalProvider>
            }
          />

          <Route
            path={RoutesPath.ExchangeUnauthorized}
            element={
              <UnauthorizedPage
                onRedirect={() => {
                  window.location.href = "https://dashtodash.com/";
                }}
              />
            }
          />

          {/* SSO Routes */}
          <Route path={RoutesPath.SSOCallback} element={<SSOCallback />} />

          {/* New-account conversion page (direct + SSO signups land here once) */}
          <Route
            path={RoutesPath.AccountCreated}
            element={<AccountCreated />}
          />
        </Routes>
      </Suspense>
    </ChunkErrorBoundary>
  ) : null;
}

export default App;
